import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Link,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  ThemeProvider,
  CssBaseline,
  useMediaQuery
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon
} from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import createAghrbaTheme from '../../../theme';
import logo from '../../../assets/images/logo_512.png';
import trackingService from '../../../services/trackingService';

/**
 * Thank you page displayed after successful form submission with Aghrba branding
 */
const ThankYouPage = () => {
  const { shareToken } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Store location state in React state to persist during theme toggles
  const [submissionData, setSubmissionData] = useState(() => {
    // First try to get from location state (React Router)
    if (location.state?.submissionId) {
      console.log('🎉 [ThankYouPage] Using submission data from location.state');
      return {
        submissionId: location.state.submissionId,
        customMessage: location.state.message,
        formTitle: location.state.formTitle || 'Form'
      };
    }
    
    // If not available, try to load from localStorage (fallback)
    try {
      const storedData = localStorage.getItem('form_submission_data');
      if (storedData) {
        console.log('🎉 [ThankYouPage] Using submission data from localStorage');
        const parsedData = JSON.parse(storedData);
        return {
          submissionId: parsedData.submissionId,
          customMessage: parsedData.message,
          formTitle: parsedData.formTitle || 'Form'
        };
      }
    } catch (err) {
      console.error('🎉 [ThankYouPage] Error loading from localStorage:', err);
    }
    
    // Default fallback values
    console.log('🎉 [ThankYouPage] No submission data found, using defaults');
    return {
      submissionId: null,
      customMessage: null,
      formTitle: 'Form'
    };
  });
  
  // Initialize theme mode from location state or localStorage
  const [themeMode, setThemeMode] = useState(() => {
    // First try to get theme from location state (passed from form page)
    if (location.state?.themeMode) {
      return location.state.themeMode;
    }
    
    // Otherwise use stored value or system preference
    const storedTheme = localStorage.getItem('aghrba-form-theme');
    if (storedTheme) return storedTheme;
    
    // Check system preference
    const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDarkMode ? 'dark' : 'light';
  });
  
  // Create theme based on current mode
  const theme = createAghrbaTheme(themeMode);
  
  // Detect if on mobile
  const isMobile = useMediaQuery(theme.breakpoints?.down('sm') || '(max-width: 600px)');
  
  // Track thank you page view when component mounts
  useEffect(() => {
    if (submissionData.submissionId && submissionData.formTitle) {
      trackingService.trackCustomEvent('FormSubmissionComplete', {
        form_id: shareToken,
        form_title: submissionData.formTitle,
        submission_id: submissionData.submissionId,
        form_type: 'public',
        share_token: shareToken,
        thank_you_page: true
      });
    }
  }, [submissionData, shareToken]);
  
  // Toggle between light and dark theme
  const toggleThemeMode = () => {
    const newThemeMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newThemeMode);
    localStorage.setItem('aghrba-form-theme', newThemeMode);
    
    // We don't need to update the history state anymore since we're using React state
    // The component won't re-render when using window.history.replaceState
  };
  
  // Effects to handle URL params and redirections
  useEffect(() => {
    // If no submission ID, redirect to form - but with a check to prevent loops
    const pageLoadCount = parseInt(sessionStorage.getItem('thank_you_page_load_count') || '0', 10);
    sessionStorage.setItem('thank_you_page_load_count', (pageLoadCount + 1).toString());
    
    if (!submissionData.submissionId && shareToken) {
      // Only redirect if we haven't tried too many times (prevents redirect loops)
      if (pageLoadCount < 2) {
        console.log('🎉 [ThankYouPage] No submission ID found, redirecting to form');
        navigate(`/forms/shared/${shareToken}`);
      } else {
        console.log('🎉 [ThankYouPage] Multiple redirection attempts detected, staying on page');
      }
    }
    
    // Set a flag that we've successfully loaded the thank you page
    if (submissionData.submissionId) {
      try {
        localStorage.setItem('thank_you_page_loaded', 'true');
        // Reset the page load counter when we have valid data
        sessionStorage.setItem('thank_you_page_load_count', '0');
        console.log('🎉 [ThankYouPage] Successfully loaded with valid submission data');
      } catch (err) {
        console.error('🎉 [ThankYouPage] Error setting thank you page flag:', err);
      }
    }
    
    // Only clean up localStorage when navigating away from thank you page
    // NOT during component unmount during initial render/rerender
    const cleanupCallback = () => {
      try {
        if (localStorage.getItem('thank_you_page_loaded') === 'true') {
          console.log('🎉 [ThankYouPage] Cleaning up localStorage on navigation away');
          localStorage.removeItem('form_submission_data');
          localStorage.removeItem('form_submission_timestamp');
          localStorage.removeItem('thank_you_page_loaded');
        }
      } catch (err) {
        console.error('🎉 [ThankYouPage] Error cleaning localStorage:', err);
      }
    };

    // Add beforeunload event listener for when user leaves the page
    window.addEventListener('beforeunload', cleanupCallback);
    
    return () => {
      window.removeEventListener('beforeunload', cleanupCallback);
      // We no longer clean up on component unmount
      // This prevents issues when the component remounts but should still show the thank you message
    };
  }, [submissionData.submissionId, shareToken, navigate]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          backgroundImage: `linear-gradient(${theme.aghrba.gradients.pageBackground})`,
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Header with branding and theme toggle */}
        <AppBar 
          position="static" 
          color="transparent" 
          elevation={0}
          sx={{ 
            bgcolor: theme.palette.mode === 'dark' 
              ? 'rgba(20, 20, 30, 0.95)' 
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid',
            borderColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(0, 0, 0, 0.1)',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.3)'
              : '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  '&::after': theme.palette.mode === 'dark' ? {
                    content: '""',
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
                    filter: 'blur(8px)',
                    transform: 'scale(1.2)',
                    opacity: 0.5,
                    zIndex: -1
                  } : {}
                }}
              >
                <img 
                  src={logo} 
                  alt="Aghrba Logo" 
                  style={{ 
                    height: 40, 
                    marginRight: 16,
                    filter: theme.palette.mode === 'dark' ? 'drop-shadow(0 0 8px rgba(255, 215, 0, 0.3))' : 'none',
                  }} 
                />
              </Box>
              {/* Removed company name text to keep only the logo */}
            </Box>
            
            {/* Theme toggle - simplified styling */}
            <Tooltip title={`Switch to ${theme.palette.mode === 'dark' ? 'light' : 'dark'} mode`}>
              <IconButton 
                onClick={toggleThemeMode} 
                color={theme.palette.mode === 'dark' ? 'default' : 'primary'}
                aria-label="Toggle theme"
                size="medium"
              >
                {theme.palette.mode === 'dark' ? (
                  <LightModeIcon fontSize="small" />
                ) : (
                  <DarkModeIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
        
        {/* Main content - simplified layout */}
        <Box 
          sx={{ 
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            pt: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 }
          }}
        >
          <Container 
            maxWidth="sm" 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              height: '100%'
            }}
          >
            <Paper 
              elevation={4} 
              sx={{ 
                p: { xs: 3, sm: 4 }, 
                textAlign: 'center',
                borderRadius: 2,
                boxShadow: theme.palette.mode === 'dark' 
                  ? theme.aghrba.effects.cardGlow 
                  : '0 8px 24px rgba(0, 0, 0, 0.1)',
                border: theme.palette.mode === 'dark' 
                  ? '1px solid rgba(255, 255, 255, 0.1)' 
                  : '1px solid rgba(0, 0, 0, 0.05)',
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(20, 20, 30, 0.9)' 
                  : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(8px)',
                overflow: 'hidden'
              }}
            >
            {/* Success icon with advanced celebration animations */}
            <Box
              sx={{
                position: 'relative',
                mb: 3,
                height: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // Outer glow
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: theme.palette.mode === 'dark' 
                    ? 'radial-gradient(circle, rgba(228, 180, 76, 0.2) 0%, rgba(228, 180, 76, 0) 70%)'
                    : 'radial-gradient(circle, rgba(228, 180, 76, 0.1) 0%, rgba(228, 180, 76, 0) 70%)',
                  animation: 'pulse-gold 2s infinite',
                  zIndex: 0
                },
                // Particles effect
                '.particle': {
                  position: 'absolute',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: theme.palette.secondary.main,
                  opacity: 0,
                  zIndex: 5
                }
              }}
            >
              {/* Success check icon */}
              <CheckCircleIcon 
                sx={{ 
                  fontSize: 80, 
                  background: theme.aghrba.gradients.goldToGreen,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  position: 'relative',
                  zIndex: 10,
                  animation: 'pop-in 0.5s forwards'
                }} 
              />
              
              {/* Generate celebration particles */}
              {[...Array(12)].map((_, i) => (
                <Box 
                  key={i}
                  className="particle"
                  sx={{
                    background: i % 3 === 0 
                      ? theme.palette.secondary.main 
                      : i % 3 === 1 
                        ? theme.palette.primary.main 
                        : '#e4b44c',
                    animation: `particle-${i % 4} 1.5s ease-out ${i * 0.08}s forwards`,
                    width: i % 2 === 0 ? 8 : 5,
                    height: i % 2 === 0 ? 8 : 5,
                  }}
                />
              ))}
              
              {/* Celebration ring */}
              <Box 
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(228, 180, 76, 0.5)' : 'rgba(228, 180, 76, 0.3)'}`,
                  opacity: 0,
                  animation: 'ring-expand 0.8s 0.2s forwards'
                }}
              />
            </Box>
            
            <Typography 
              variant="h3" 
              gutterBottom
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mt: 1
              }}
            >
              Thank You!
            </Typography>
            
            <Typography 
              variant="h5" 
              gutterBottom
              sx={{
                fontWeight: 500,
                mb: 2,
                color: theme.palette.text.secondary
              }}
            >
              {submissionData.formTitle} Submitted Successfully
            </Typography>
            
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Your response has been submitted successfully.
            </Typography>
            
            {submissionData.customMessage && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box 
                  sx={{ 
                    p: 2, 
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)', 
                    borderRadius: 1,
                    mb: 3
                  }}
                >
                  <Typography variant="body1">
                    {submissionData.customMessage}
                  </Typography>
                </Box>
              </>
            )}
            
            {submissionData.submissionId && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Submission ID:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    display: 'inline-block'
                  }}
                >
                  {submissionData.submissionId}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              {shareToken && (
                <Button 
                  variant="outlined" 
                  onClick={() => navigate(`/forms/shared/${shareToken}`)}
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    }
                  }}
                >
                  Submit Another Response
                </Button>
              )}
              
              <Button 
                variant="contained" 
                onClick={() => window.close()}
                sx={{
                  backgroundImage: `linear-gradient(${theme.aghrba.gradients.primaryButton})`,
                  '&:hover': {
                    boxShadow: theme.aghrba.effects.buttonHover,
                  }
                }}
              >
                Close
              </Button>
            </Box>
            
            </Paper>
            
            {/* Theme toggle - moved outside the form for cleaner UI */}
            <Box 
              sx={{ 
                display: 'flex',
                justifyContent: 'center',
                mt: 3
              }}
            >
              <Button 
                onClick={toggleThemeMode}
                startIcon={theme.palette.mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                variant="text"
                size="small"
                color="secondary"
                sx={{ textTransform: 'none' }}
              >
                {theme.palette.mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              </Button>
            </Box>
          </Container>
        </Box>
        
        {/* Main footer - outside the form card, at bottom of page */}
        <Box 
          component="footer"
          sx={{ 
            py: 2,
            mt: 'auto',
            borderTop: '1px solid',
            borderColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(0, 0, 0, 0.1)',
            bgcolor: theme.palette.mode === 'dark' 
              ? 'rgba(20, 20, 30, 0.95)' 
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 -4px 20px rgba(0, 0, 0, 0.4)'
              : '0 -4px 20px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  position: 'relative',
                  mb: { xs: 1, sm: 0 }
                }}
              >
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    '&::after': theme.palette.mode === 'dark' ? {
                      content: '""',
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
                      filter: 'blur(8px)',
                      transform: 'scale(1.2)',
                      opacity: 0.5,
                      zIndex: -1
                    } : {}
                  }}
                >
                  <img 
                    src={logo} 
                    alt="Aghrba Logo" 
                    style={{ 
                      height: 24, 
                      marginRight: 8,
                      filter: theme.palette.mode === 'dark' ? 'drop-shadow(0 0 5px rgba(255, 215, 0, 0.3))' : 'none'
                    }} 
                  />
                </Box>
                {/* Removed company name text to keep only the logo */}
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', sm: 'flex-end' } }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <span 
                    style={{ 
                      color: theme.palette.secondary.main,
                      fontWeight: '500'
                    }}
                  >
                    © {new Date().getFullYear()} All Rights Reserved
                  </span>
                  <Box 
                    component="span" 
                    sx={{ 
                      mx: 0.5, 
                      width: '4px', 
                      height: '4px', 
                      borderRadius: '50%', 
                      bgcolor: 'text.disabled',
                      display: 'inline-block'
                    }} 
                  />
                  <span>
                    <Link 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        toggleThemeMode();
                      }}
                      sx={{ 
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {theme.palette.mode === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
                    </Link>
                  </span>
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
      
      {/* Global CSS for animations */}
      <style jsx global>{`
        @keyframes pulse-gold {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.8;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.5);
            opacity: 0.3;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.8;
          }
        }
        
        @keyframes pop-in {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          60% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        @keyframes ring-expand {
          0% {
            transform: translate(-50%, -50%) scale(0.5);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0;
          }
        }
        
        @keyframes particle-0 {
          0% {
            transform: translate(0, 0);
            opacity: 1;
          }
          100% {
            transform: translate(-40px, -40px);
            opacity: 0;
          }
        }
        
        @keyframes particle-1 {
          0% {
            transform: translate(0, 0);
            opacity: 1;
          }
          100% {
            transform: translate(40px, -40px);
            opacity: 0;
          }
        }
        
        @keyframes particle-2 {
          0% {
            transform: translate(0, 0);
            opacity: 1;
          }
          100% {
            transform: translate(40px, 40px);
            opacity: 0;
          }
        }
        
        @keyframes particle-3 {
          0% {
            transform: translate(0, 0);
            opacity: 1;
          }
          100% {
            transform: translate(-40px, 40px);
            opacity: 0;
          }
        }
        
        @keyframes shimmer {
          0% {
            background-position: -100% 0;
            text-shadow: 0 0 10px rgba(228, 180, 76, 0.2);
          }
          50% {
            text-shadow: 0 0 20px rgba(228, 180, 76, 0.5);
          }
          100% {
            background-position: 200% 0;
            text-shadow: 0 0 10px rgba(228, 180, 76, 0.2);
          }
        }
        
        /* Cross-browser animation compatibility */
        .particle {
          animation-fill-mode: forwards !important;
          -webkit-animation-fill-mode: forwards !important;
        }
        
        /* Browser-specific prefixes */
        @-webkit-keyframes pulse-gold {
          0% {
            -webkit-transform: translate(-50%, -50%) scale(1);
            opacity: 0.8;
          }
          50% {
            -webkit-transform: translate(-50%, -50%) scale(1.5);
            opacity: 0.3;
          }
          100% {
            -webkit-transform: translate(-50%, -50%) scale(1);
            opacity: 0.8;
          }
        }
        
        @-webkit-keyframes pop-in {
          0% {
            -webkit-transform: scale(0);
            opacity: 0;
          }
          60% {
            -webkit-transform: scale(1.2);
          }
          100% {
            -webkit-transform: scale(1);
            opacity: 1;
          }
        }
        
        @-webkit-keyframes ring-expand {
          0% {
            -webkit-transform: translate(-50%, -50%) scale(0.5);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            -webkit-transform: translate(-50%, -50%) scale(1.2);
            opacity: 0;
          }
        }
        
        /* Fix for Safari and older browsers */
        @media screen and (-webkit-min-device-pixel-ratio: 0) {
          .particle {
            animation-delay: 0.01s !important;
          }
        }
      `}</style>
    </ThemeProvider>
  );
};

export default ThankYouPage;